import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Building2, MapPin, BookOpen, FileText, LogOut, Menu, X, Zap, Database, UserCircle2, BarChart3, AlertCircle } from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout, canCreateRapport, hasPermission, userPermissions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOuvert, setMenuOuvert] = useState({
    creer_rapport: false,
    admin: false,
    admin_ticfe: false,
    admin_turpe: false
  });

  // Redirection forcée vers /account si l'utilisateur doit changer son mot de passe
  useEffect(() => {
    if (user?.must_change_password && location.pathname !== '/account') {
      navigate('/account', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const toggleMenu = (menu) => {
    setMenuOuvert({...menuOuvert, [menu]: !menuOuvert[menu]});
  };

  // Helpers permissions
  const can = (module, action = 'view') => hasPermission && hasPermission(module, action);

  // Navigation de base
  const navigation = [];
  if (can('dashboard', 'view')) {
    navigation.push({
      name: 'Tableau de bord',
      href: '/dashboard',
      icon: LayoutDashboard,
      type: 'link'
    });
    navigation.push({
      name: 'Analyses',
      href: '/analyses',
      icon: BarChart3,
      type: 'link'
    });
  }
  
  // "Créer Rapport" : visible si au moins une permission de création
  const canCreateAny = can('ticfe_rapports', 'create') || can('ticgn_rapports', 'create') || can('turpe_rapports', 'create');
  if (canCreateAny) {
    const createItems = [];
    if (can('ticfe_rapports', 'create')) createItems.push({ name: 'Rapport TICFE', href: '/rapports/create', icon: FileText });
    if (can('ticgn_rapports', 'create')) createItems.push({ name: 'Rapport TICGN', href: '/rapports/ticgn/create', icon: FileText });
    if (can('turpe_rapports', 'create')) createItems.push({ name: 'Analyse TURPE', href: '/turpe/create', icon: Zap });
    if (createItems.length > 0) {
      navigation.push({
        name: 'Créer Rapport',
        icon: FileText,
        type: 'menu',
        key: 'creer_rapport',
        items: createItems
      });
    }

    // TURPE Massif (admin only — scope all)
    if (userPermissions?.isSystem) {
      navigation.push({
        name: 'TURPE Massif',
        href: '/turpe-massif',
        icon: Database,
        type: 'link'
      });
    }
  }

  // Menu "Codes NAF" : consultable par tous ceux qui ont la perm codes_naf.view
  if (can('codes_naf', 'view')) {
    navigation.push({
      name: 'Codes NAF',
      href: '/admin/codes-naf',
      icon: BookOpen,
      type: 'link'
    });
  }

  // Menu Administration : visible si au moins un droit admin_*
  const adminItems = [];
  const ticfeAdmin = [];
  if (can('admin_codes_naf', 'view')) ticfeAdmin.push({ name: 'Codes NAF', href: '/admin/codes-naf', icon: BookOpen });
  if (can('admin_baremes', 'view')) ticfeAdmin.push({ name: 'Barèmes TICFE', href: '/admin/baremes-ticfe' });
  if (can('admin_tarifs', 'view')) ticfeAdmin.push({ name: 'Tarif CERFA', href: '/admin/tarifs-cerfa' });
  if (can('admin_baremes', 'view')) ticfeAdmin.push({ name: 'Matrice Taux', href: '/admin/matrice-taux' });
  if (ticfeAdmin.length > 0) adminItems.push({ name: 'TICFE', type: 'submenu', key: 'admin_ticfe', items: ticfeAdmin });

  const turpeAdmin = [];
  if (can('admin_turpe', 'view')) turpeAdmin.push({ name: 'Coefficients Pondérateurs', href: '/admin/coefficients-turpe' });
  if (can('admin_turpe', 'view')) turpeAdmin.push({ name: 'Composantes Fixes', href: '/admin/composantes-turpe' });
  if (can('admin_turpe', 'view')) turpeAdmin.push({ name: 'Énergie Réactive', href: '/admin/reactif-turpe' });
  if (can('admin_tarifs', 'view')) turpeAdmin.push({ name: 'Tarifs Enedis PS/FTA', href: '/admin/tarifs-enedis' });
  if (can('admin_turpe', 'edit')) turpeAdmin.push({ name: 'Correctif dépassements', href: '/admin/recompute-depassements' });
  if (turpeAdmin.length > 0) adminItems.push({ name: 'TURPE', type: 'submenu', key: 'admin_turpe', items: turpeAdmin });

  const ticgnAdmin = [];
  if (can('admin_baremes', 'view')) ticgnAdmin.push({ name: 'Barèmes TICGN', href: '/admin/baremes-ticgn' });
  if (can('admin_codes_naf', 'view')) ticgnAdmin.push({ name: 'Codes NAF TICGN', href: '/admin/codes-naf-ticgn' });
  if (can('admin_codes_naf', 'view')) ticgnAdmin.push({ name: 'Rubriques ICPE', href: '/admin/icpe-rubriques' });
  if (can('admin_tarifs', 'view')) ticgnAdmin.push({ name: 'Tarifs réduits TICGN', href: '/admin/tarifs-reduits-ticgn' });
  if (can('admin_tarifs', 'view')) ticgnAdmin.push({ name: 'Tarifs fiscaux énergies (VP)', href: '/admin/tarifs-fiscaux-energies' });
  if (can('admin_tarifs', 'view')) ticgnAdmin.push({ name: 'Cas Double Usage', href: '/admin/cas-double-usage-ticgn' });
  if (can('admin_tarifs', 'view')) ticgnAdmin.push({ name: 'Tarif CERFA TICGN', href: '/admin/tarifs-cerfa-ticgn' });
  if (ticgnAdmin.length > 0) adminItems.push({ name: 'TICGN', type: 'submenu', key: 'admin_ticgn', items: ticgnAdmin });

  if (userPermissions?.isSystem) {
    adminItems.push({ name: 'Commerciaux', href: '/admin/commerciaux', icon: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> });
  }
  if (can('admin_users', 'view')) {
    adminItems.push({ name: 'Utilisateurs', href: '/users', icon: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg> });
  }
  if (can('admin_audit_log', 'view')) {
    adminItems.push({ name: "Journal d'audit", href: '/admin/audit-log', icon: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> });
  }
  if (userPermissions?.isSystem) {
    adminItems.push({ name: 'Sauvegarde / Restauration', href: '/admin/backup', icon: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 4 3h8c2.5 0 4-1 4-3V7M4 7c0-2 1.5-3 4-3h8c2.5 0 4 1 4 3M4 7c0 2 1.5 3 4 3h8c2.5 0 4-1 4-3M12 12v6m0 0l-2.5-2.5M12 18l2.5-2.5" /></svg> });
  }

  if (adminItems.length > 0) {
    navigation.push({
      name: 'Administration',
      icon: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      type: 'menu',
      key: 'admin',
      items: adminItems
    });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <header className="bg-white border-b border-[#BFC6CC] sticky top-0 z-50 h-16 md:h-20" data-testid="dashboard-header">
        <div className="h-full px-6 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 hover:bg-[#F5F6F9] rounded-lg transition-colors"
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <img src="/logo.jpg" alt="PDE Logo" className="h-10 md:h-12" />
            <h1 className="text-xl md:text-2xl font-heading font-semibold text-[#191D1E] tracking-tight">
              Gestion des Optimisations
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/account"
              className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-[#687175] hover:bg-[#F5F6F9] rounded-lg transition-colors"
              data-testid="account-link"
              title="Paramètres du compte"
            >
              <UserCircle2 className="w-4 h-4" />
              <span>{user?.name}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#687175] hover:bg-[#F5F6F9] rounded-lg transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className={`fixed md:sticky top-16 md:top-20 left-0 z-40 w-64 h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] bg-[#F5F6F9] border-r border-[#BFC6CC] transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} data-testid="dashboard-sidebar">
          <nav className="p-4 space-y-1">
            {navigation.map((item) => {
              if (item.type === 'link') {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                      isActive ? 'bg-[#397DFF] text-white' : 'text-[#687175] hover:bg-[#BFC6CC]/30'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              }
              
              if (item.type === 'menu') {
                const isOpen = menuOuvert[item.key];
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.key)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium text-sm text-[#687175] hover:bg-[#BFC6CC]/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        {item.name}
                      </div>
                      <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.items.map((subitem) => {
                          if (subitem.type === 'submenu') {
                            const isSubOpen = menuOuvert[subitem.key];
                            return (
                              <div key={subitem.name}>
                                <button
                                  onClick={() => toggleMenu(subitem.key)}
                                  className="w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm text-[#687175] hover:bg-[#BFC6CC]/30"
                                >
                                  <span>{subitem.name}</span>
                                  <svg className={`w-3 h-3 transition-transform ${isSubOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                {isSubOpen && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {subitem.items.map((subsubitem) => {
                                      const isActive = location.pathname === subsubitem.href;
                                      return (
                                        <Link
                                          key={subsubitem.name}
                                          to={subsubitem.href}
                                          onClick={() => setSidebarOpen(false)}
                                          className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                                            isActive ? 'bg-[#397DFF] text-white' : 'text-[#687175] hover:bg-[#BFC6CC]/30'
                                          }`}
                                        >
                                          {subsubitem.name}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          const isActive = location.pathname === subitem.href;
                          return (
                            <Link
                              key={subitem.name}
                              to={subitem.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                                isActive ? 'bg-[#397DFF] text-white' : 'text-[#687175] hover:bg-[#BFC6CC]/30'
                              } ${subitem.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {subitem.icon && <subitem.icon className="w-4 h-4" />}
                              {subitem.name}
                              {subitem.badge && (
                                <span className="ml-auto text-xs bg-[#F9C80E] text-white px-2 py-0.5 rounded-full">
                                  {subitem.badge}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return null;
            })}
          </nav>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 px-6 md:px-8 lg:px-12 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
